import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { Link, useParams, useLocation } from 'react-router-dom';
import { get, post } from '../../api/http';
import { formatDate as formatEventDate, formatTime as formatEventTime } from '../../utils/dateUtils';
import { HomePageAssets } from '../../assets/homePageIcons';
import ExternalEventModal from '../../components/ExternalEventModal';
import Header from '../../components/Header';
import {
  CANCEL_ACTIVE_SESSION_URL,
  CHECK_ACTIVE_SESSION_URL,
  CHECKOUT_SESSION_STATUS_URL,
  FREE_BOOKING_ELIGIBILITY_URL,
  PUBLIC_CHECKOUT_URL,
  PUBLIC_EVENT_DETAIL_URL,
  PUBLIC_SESSION_STATUS_URL,
  RESUME_SESSION_URL,
  SUBMIT_FREE_BOOKING_URL,
  VALIDATE_PROMO_CODE_URL
} from '../../constants/apiUrls';
import { Event, EventDetailResponse, Ticket } from '../../types/event';
import './EventDetails.css';
import './EventDetailsExternal.css';
import {
  ActiveSessionCheckResponse,
  ActiveSessionInfo,
  CheckoutResponse,
  FreeBookingEligibilityResponse,
  FreeBookingResponse,
  PromoCodeValidationResponse,
  SessionStatusResponse
} from '../../types/event-details';
import { MAX_TICKETS_PER_PURCHASE,LOCAL_STORAGE_SESSION_KEY,LOCAL_STORAGE_EXPIRY_KEY } from '../../constants/events';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

 


// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const EventDetails: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { eventUuid } = useParams<{ eventUuid: string }>();
  const location = useLocation();
  const inviteToken = (location.state as any)?.inviteToken;
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [ticketCounts, setTicketCounts] = useState<{ [key: number]: number }>({});
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [attendees, setAttendees] = useState<Array<{ id: number; email: string; name: string; ticketId: number | null }>>([]);
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [modalAttendee, setModalAttendee] = useState<{ id: number; email: string; name: string; ticketId: number | null } | null>(null);
  const [showFreeBookingModal, setShowFreeBookingModal] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [freeBookingSuccess, setFreeBookingSuccess] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState<string | null>(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [showExternalModal, setShowExternalModal] = useState(false);
  
  // Session management states
  const [reservationExpiresAt, setReservationExpiresAt] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [activeSessionInfo, setActiveSessionInfo] = useState<ActiveSessionInfo | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Promo code states
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState<PromoCodeValidationResponse | null>(null);
  
  const statusCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);

  const generateReceiptOrderId = (isFreeOrder: boolean): string => {
    if (!event) return '';
    const prefix = isFreeOrder ? 'FREE' : 'ORD';
    return `#${prefix}-${event.id}-${Date.now().toString().slice(-6)}`;
  };

  // Auto-scroll to active session warning
  useEffect(() => {
    if (activeSessionInfo) {
      setTimeout(() => {
        document.getElementById('active-session-warning')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [activeSessionInfo]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!eventUuid) return;
      
      try {
        setLoading(true);
        const response = await get<EventDetailResponse>(PUBLIC_EVENT_DETAIL_URL(eventUuid));
        setEvent(response.data.event);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch event details', err);
        setError('Failed to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
    checkLocalStorageSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUuid]);

  // Inject dynamic Open Graph meta tags for rich link previews (WhatsApp, Telegram, etc.)
  useEffect(() => {
    if (!event) return;

    const pageUrl = window.location.href;
    const imageUrl = event.featured_image?.file_url ?? '';
    const description = event.info
      ? event.info.replace(/<[^>]*>/g, '').slice(0, 200)
      : `Join us at ${event.event_title}`;

    const ogTags: { property: string; content: string }[] = [
      { property: 'og:title',       content: event.event_title },
      { property: 'og:description', content: description },
      { property: 'og:image',       content: imageUrl },
      { property: 'og:url',         content: pageUrl },
      { property: 'og:type',        content: 'website' },
    ];

    const inserted: HTMLMetaElement[] = [];

    ogTags.forEach(({ property, content }) => {
      // Update existing tag if present, otherwise insert a new one
      let tag = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
        inserted.push(tag);
      }
    });

    // Also update the page title for better previews
    const previousTitle = document.title;
    document.title = event.event_title;

    return () => {
      // Remove only tags that this effect created; restore the page title
      inserted.forEach((tag) => document.head.removeChild(tag));
      document.title = previousTitle;
    };
  }, [event]);

  const handleGetDirection = () => {
    if (!event) return;
    
    if (event.location_coordinates && (event.location_coordinates.latitude || event.location_coordinates.longitude)) {
      const { latitude, longitude } = event.location_coordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`, '_blank');
    } else if (event.address || event.venue) {
       const query = event.address || event.venue;
       window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query || "")}`, '_blank');
    }
  };

  const handleExternalClick = () => {
    if (event?.external_ticketing_url) {
      setShowExternalModal(true);
    }
  };

  const handleExternalConfirm = () => {
    if (event?.external_ticketing_url) {
      window.open(event.external_ticketing_url, '_blank');
    }
    setShowExternalModal(false);
  };

  const checkLocalStorageSession = async () => {
    const storedSessionId = localStorage.getItem(LOCAL_STORAGE_SESSION_KEY);
    const storedExpiry = localStorage.getItem(LOCAL_STORAGE_EXPIRY_KEY);

    if (storedSessionId && storedExpiry) {
      const expiryDate = new Date(storedExpiry);
      const now = new Date();

      if (expiryDate > now) {
        // Session might still be valid, check status
        try {
          const response = await get<SessionStatusResponse>(PUBLIC_SESSION_STATUS_URL(storedSessionId));
          const { success, status, payment_status, amount_total, customer_email } = response.data;
          
          if (success && status === 'open' && payment_status === 'unpaid') {
            console.log('Restoring session from local storage:', storedSessionId);
            
            // Construct session info object compatible with ActiveSessionInfo
            const sessionInfo: ActiveSessionInfo = {
              session_id: storedSessionId,
              event_id: Number(event?.id || 0), // Note: might not have event loaded yet
              email: customer_email || '',
              created_at: new Date(now.getTime() - ((10 * 60 * 1000) - (expiryDate.getTime() - now.getTime()))).toISOString(), // Estimate creation
              amount_total: amount_total,
              expires_at: storedExpiry
            };
            
            setActiveSessionInfo(sessionInfo);
          } else {
            // Session expired or completed, clear invalid local storage
            clearLocalStorageSession();
          }
        } catch (error: any) {
          console.warn('Failed to validate local storage session:', error);
          // Only clear if we receive a definitive error indicating the session doesn't exist or is invalid
          // e.g. 404 Not Found, 400 Bad Request
          if (error.response && (error.response.status === 404 || error.response.status === 400)) {
            clearLocalStorageSession();
          }
          // For network errors, we don't clear, giving the user a chance to refresh
        }
      } else {
        // Expired locally
        clearLocalStorageSession();
      }
    } else {
      // Fallback to API check if nothing in local storage
      checkForActiveSession();
    }
  };

  const clearLocalStorageSession = () => {
    localStorage.removeItem(LOCAL_STORAGE_SESSION_KEY);
    localStorage.removeItem(LOCAL_STORAGE_EXPIRY_KEY);
  };

  // Session Status Polling
  useEffect(() => {
    if (sessionId && showCheckout) {
      statusCheckInterval.current = setInterval(async () => {
        try {
          const response = await get<SessionStatusResponse>(CHECKOUT_SESSION_STATUS_URL(sessionId));
          if (response.data.status === 'complete') {
            // Success! Handle post-payment
             if (statusCheckInterval.current) clearInterval(statusCheckInterval.current);
             
             const currentSessionId = sessionId;
             setShowCheckout(false);
             setSessionId(null);
             setClientSecret(null);
             setReservationExpiresAt(null);
             setActiveSessionInfo(null);
             clearLocalStorageSession();

             // Show success modal
             setReceiptOrderId(generateReceiptOrderId(false));
             setCheckoutSuccess(true);
             
             // Trigger cancelActiveSession API to clear backend session state immediately
             const emailForCancel = activeSessionInfo?.email || buyerEmail || localStorage.getItem('customerEmail');
             if (emailForCancel && currentSessionId) {
               try {
                 await post(CANCEL_ACTIVE_SESSION_URL, { email: emailForCancel, session_id: currentSessionId });
               } catch (cancelErr) {
                 console.warn('Post-payment session cleanup failed:', cancelErr);
               }
             }
          } else if (response.data.status === 'expired') {
            handleReservationExpired();
          }
        } catch (error) {
          console.warn('Status check failed:', error);
        }
      }, 2000);
    }

    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, showCheckout]);

  const checkForActiveSession = async () => {
    try {
      let email = localStorage.getItem('customerEmail');
      // If we have an email in state (e.g. from user input), use it
      if (!email && buyerEmail) {
        email = buyerEmail;
      }

      const params = new URLSearchParams();
      if (email) {
        params.append('email', email);
      }
      
      // Also send eventUuid to scope the check if needed by backend, 
      // although snippets just used email or session cookie
      if (eventUuid) {
        params.append('event_uuid', eventUuid);
      }

      console.log('Checking for active session...');
      const response = await get<ActiveSessionCheckResponse>(`${CHECK_ACTIVE_SESSION_URL}?${params.toString()}`);
      
      if (response.data.hasActiveSession && response.data.sessionInfo) {
        console.log('Found active session:', response.data.sessionInfo);
        setActiveSessionInfo(response.data.sessionInfo);
      } else {
        setActiveSessionInfo(null);
      }
    } catch (error) {
      console.warn('Failed to check active session:', error);
    }
  };

  const resumeSession = async (sessionId: string) => {
    setIsProcessing(true);
    try {
      const response = await get<SessionStatusResponse>(RESUME_SESSION_URL(sessionId));
      
      const r = response.data;
      const clientSecret = r.clientSecret || r.client_secret;
      const pubKey = r.publishableKey || r.publishable_key || process.env.REACT_APP_STRIPE_PUBLIC_KEY;
      
      let expiry: Date | undefined;
      if (r.reservationExpiresAt) {
        expiry = new Date(r.reservationExpiresAt);
      } else if (r.expires_at) {
         expiry = new Date(r.expires_at * 1000);
      }

      if (clientSecret && pubKey) {
        setClientSecret(clientSecret);
        if (expiry) {
          setReservationExpiresAt(expiry);
        }
        
        try {
          const stripe = await loadStripe(pubKey);
          
          if (stripe) {
            setStripeInstance(stripe);
            setSessionId(sessionId);
            setShowCheckout(true);
            setActiveSessionInfo(null); // Clear warning as we are resuming

            // Scroll to checkout container
            setTimeout(() => {
              const checkoutContainer = document.querySelector('.embedded-checkout-container');
              if (checkoutContainer) {
                checkoutContainer.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
            
          } else {
            throw new Error('Failed to load Stripe instance');
          }
        } catch (stripeError) {
           console.error("Stripe loading failed during resume:", stripeError);
           // Cancel session on resume failure too
           const email = activeSessionInfo?.email || buyerEmail || localStorage.getItem('customerEmail');
           if (email) {
              await post(CANCEL_ACTIVE_SESSION_URL, { email, session_id: sessionId });
           }
           clearLocalStorageSession();
           setSessionId(null);
           setClientSecret(null);
           setActiveSessionInfo(null);
           throw new Error("Failed to load payment system. Session cancelled.");
        }
      } else {
        throw new Error('Failed to resume session - missing credentials');
      }
    } catch (error: any) {
      console.error('Resume failed:', error);
      setCheckoutError('Failed to resume session: ' + (error.message || 'Unknown error'));
      setActiveSessionInfo(null); 
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelActiveSession = async () => {
    setIsProcessing(true);
    try {
      // Prioritize email from the active session info if available
      const email = activeSessionInfo?.email || buyerEmail || localStorage.getItem('customerEmail');
      const sessionId = activeSessionInfo?.session_id || localStorage.getItem(LOCAL_STORAGE_SESSION_KEY);
      
      if (!email && !sessionId) {
        throw new Error('Could not identify session to cancel');
      }
      
      await post(CANCEL_ACTIVE_SESSION_URL, { email, session_id: sessionId });
      setActiveSessionInfo(null);
      setCheckoutError(null);
      setShowCancelConfirmation(false);
      clearLocalStorageSession();
      setTicketCounts({}); // Reset ticket selection
      
    } catch (error: any) {
      console.error('Cancellation failed:', error);
      setCheckoutError('Failed to cancel session: ' + (error.message || 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (dateString: string, timeZone?: string) => formatEventTime(dateString, timeZone);
  const formatDateShort = (dateString: string, timeZone?: string) => formatEventDate(dateString, timeZone);

  const isValidDate = (value: Date): boolean => !Number.isNaN(value.getTime());

  const _toLocalIsoString = (utcIsoString: string): string | null => {
    const date = new Date(utcIsoString);
    if (!isValidDate(date)) return null;

    const pad2 = (n: number): string => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad2(date.getMonth() + 1);
    const day = pad2(date.getDate());
    const hours = pad2(date.getHours());
    const minutes = pad2(date.getMinutes());
    const seconds = pad2(date.getSeconds());

    // Local "wall-clock" ISO-like string (no offset / no Z)
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const formatLocalIsoDateTime = (localIsoString: string, timeZoneLabel?: string): string | null => {
    // Parse as local time (since there's no Z/offset).
    const date = new Date(localIsoString);
    if (!isValidDate(date)) return null;

    // Expected output: "Mon, March 30 2026, 11:00 PM"
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
    const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
    const day = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(date);
    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date);
    const time = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);

    const base = `${weekday}, ${month} ${day} ${year}, ${time}`;
    const tzLabel = timeZoneLabel?.trim();
    return tzLabel ? `${base} (${tzLabel})` : base;
  };

  const getTicketSaleMessage = (ticket: Ticket, eventTimeZoneLabel: string): string | null => {
    const startRaw = ticket.start_at;
    const endRaw = ticket.expired_at;

    if (!startRaw) return null;

    const start = new Date(startRaw);
    const end = endRaw ? new Date(endRaw) : null;
    const now = new Date();

    if (!isValidDate(start) || (end && !isValidDate(end))) return null;

    if (end && end.getTime() < start.getTime()) {
      return 'Invalid ticket schedule. Please contact support.';
    }

    if (now.getTime() < start.getTime()) {
      const localIso = _toLocalIsoString(startRaw);
      if (!localIso) return null;
      const formatted = formatLocalIsoDateTime(localIso, eventTimeZoneLabel);
      return formatted ? `Ticket sales start ${formatted}` : null;
    }

    if (end) {
      if (now.getTime() <= end.getTime()) {
        const localIso = _toLocalIsoString(endRaw ?? '');
        if (!localIso) return null;
        const formatted = formatLocalIsoDateTime(localIso, eventTimeZoneLabel);
        return formatted ? `Ticket sales end ${formatted}` : null;
      }
      const localIso = _toLocalIsoString(endRaw ?? '');
      if (!localIso) return null;
      const formatted = formatLocalIsoDateTime(localIso, eventTimeZoneLabel);
      return formatted ? `Ticket sales ended ${formatted}` : null;
    }

    // If there's no end date, we can't communicate an end-time reliably.
    return null;
  };

  // Check if event has expired
  const isEventExpired = () => {
    if (!event?.end) return false;
    const eventEndDate = new Date(event.end);
    const now = new Date();
    return now > eventEndDate;
  };

  // Get all media items (featured image + additional medias)
  const getAllMedia = () => {
    const mediaItems: Array<{ type: string; url: string; id: number | string }> = [];
    
    if (event?.featured_image) {
      mediaItems.push({
        type: 'image',
        url: event.featured_image.file_url,
        id: 'featured'
      });
    }
    
    if (event?.medias && event.medias.length > 0) {
      event.medias.forEach((media, index) => {
        mediaItems.push({
          type: media.media_type?.toLowerCase() || 'image',
          url: media.file_url,
          id: media.id || `media-${index}`
        });
      });
    }
    
    return mediaItems;
  };

  const handleAddTicket = (ticketId: number) => {
    const ticket = event?.tickets?.find((t) => t.id === ticketId);
    if (!ticket) return;

    // Check availability
    const currentCount = ticketCounts[ticketId] || 0;
    if (currentCount >= ticket.remaining_tickets) return;

    setTicketCounts((prev) => ({
      ...prev,
      [ticketId]: (prev[ticketId] || 0) + 1,
    }));
  };

  const handleRemoveTicket = (ticketId: number) => {
    setTicketCounts((prev) => {
      const newCount = Math.max(0, (prev[ticketId] || 0) - 1);
      if (newCount === 0) {
        const { [ticketId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketId]: newCount };
    });
  };

  const handleAddAttendee = () => {
    setModalAttendee({ id: Date.now(), email: '', name: '', ticketId: null });
    setModalError(null);
    setShowAttendeeModal(true);
  };

  const handleEditAttendee = (attendee: { id: number; email: string; name: string; ticketId: number | null }) => {
    setModalAttendee(attendee);
    setModalError(null);
    setShowAttendeeModal(true);
  };

  const handleSaveAttendee = () => {
    if (!modalAttendee) return;
    
    // Validation
    if (!modalAttendee.name || !modalAttendee.email || !modalAttendee.ticketId) {
      setModalError('Please fill in all fields.');
      return;
    }

    // Email format validation
    if (!isValidEmail(modalAttendee.email)) {
      setModalError('Please enter a valid email address (e.g., name@example.com).');
      return;
    }

    const ticketId = modalAttendee.ticketId;
    const selectedCount = ticketCounts[ticketId] || 0;
    
    // Count other attendees using this ticket (excluding current one if editing)
    const currentUsage = attendees.filter(a => a.ticketId === ticketId && a.id !== modalAttendee.id).length;
    
    if (currentUsage >= selectedCount) {
       setModalError(`You only selected ${selectedCount} ticket${selectedCount !== 1 ? 's' : ''} of this type. You cannot add more recipients than tickets purchased.`);
       return;
    }

    setAttendees((prev) => {
      const existing = prev.find((a) => a.id === modalAttendee.id);
      if (existing) {
        return prev.map((a) => (a.id === modalAttendee.id ? modalAttendee : a));
      }
      return [...prev, modalAttendee];
    });
    
    setShowAttendeeModal(false);
    setModalAttendee(null);
    setModalError(null);
  };

  const handleRemoveAttendee = (id: number) => {
    setAttendees((prev) => prev.filter((attendee) => attendee.id !== id));
  };

  const handleModalInputChange = (field: 'email' | 'name' | 'ticketId', value: string | number | null) => {
    if (!modalAttendee) return;
    setModalAttendee({ ...modalAttendee, [field]: value });
  };

  const getTotalTicketCount = () => {
    return Object.values(ticketCounts).reduce((sum, count) => sum + count, 0);
  };

  const getSelectedTickets = () => {
    if (!event?.tickets) return [];
    return event.tickets.filter((ticket) => ticketCounts[ticket.id] && ticketCounts[ticket.id] > 0);
  };

  const getAttendeesCount = () => {
    return attendees.filter((a) => a.email && a.name).length;
  };

  const calculateSubtotal = () => {
    if (!event?.tickets) return 0;
    return event.tickets.reduce((total, ticket) => {
      const count = ticketCounts[ticket.id] || 0;
      return total + (ticket.price * count);
    }, 0);
  };

  const calculatePlatformFee = (subtotal: number) => {
    return subtotal * 0.07; // 7% platform fee
  };

  const calculateProcessingFee = (subtotal: number) => {
    return subtotal * 0.0356; // ~3.56% processing fee
  };

  // Calculate promo discount amount from the validated promo response
  const calculateDiscountAmount = (): number => {
    if (!promoDiscount?.success && !promoDiscount?.valid) return 0;
    const raw =
      promoDiscount.discount_amount ??
      promoDiscount.total_discount ??
      0;
    return parseFloat(String(raw)) || 0;
  };

  // Discounted subtotal = original subtotal minus promo discount (floored at 0)
  // Platform fee and processing fee are calculated on THIS value, not the original subtotal.
  const calculateDiscountedSubtotal = (): number => {
    return Math.max(0, calculateSubtotal() - calculateDiscountAmount());
  };

  // Final total: fees are always applied to the discounted subtotal so that
  // a promo code reduces the base before the 7% platform fee is charged.
  const calculateTotal = () => {
    const discountedSubtotal = calculateDiscountedSubtotal();
    const platformFee = calculatePlatformFee(discountedSubtotal);
    const processingFee = calculateProcessingFee(discountedSubtotal);
    return discountedSubtotal + platformFee + processingFee;
  };

  // Reset promo code whenever the cart changes
  useEffect(() => {
    if (appliedPromoCode) {
      setAppliedPromoCode(null);
      setPromoDiscount(null);
      setPromoError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketCounts]);

  // Validate promo code against the API
  const validatePromoCode = async () => {
    if (!promoCode.trim() || !event) return;

    setPromoValidating(true);
    setPromoError(null);

    const cartItems = Object.entries(ticketCounts)
      .filter(([_, qty]) => qty > 0)
      .map(([ticketId, quantity]) => ({ ticket_id: Number(ticketId), quantity }));

    try {
      const response = await post<PromoCodeValidationResponse>(
        VALIDATE_PROMO_CODE_URL(event.id),
        { code: promoCode.trim(), cart_items: cartItems }
      );

      // The API signals validity via `valid` when present, otherwise falls back to `success`
      const isValid = response.data.valid !== undefined ? response.data.valid : response.data.success;

      if (isValid) {
        setAppliedPromoCode(promoCode.trim());
        setPromoDiscount(response.data);
        setPromoError(null);
      } else {
        setPromoError(response.data.message || 'Invalid promo code.');
        setAppliedPromoCode(null);
        setPromoDiscount(null);
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to validate promo code. Please try again.';
      setPromoError(msg);
      setAppliedPromoCode(null);
      setPromoDiscount(null);
    } finally {
      setPromoValidating(false);
    }
  };

  // Remove applied promo code
  const removePromoCode = () => {
    setPromoCode('');
    setAppliedPromoCode(null);
    setPromoDiscount(null);
    setPromoError(null);
  };

  // Check if all selected tickets are free
  const checkIfAllTicketsFree = () => {
    let allFree = true;
    let hasTickets = false;

    for (const [ticketId, quantity] of Object.entries(ticketCounts)) {
      if (quantity > 0) {
        hasTickets = true;
        const ticket = event?.tickets?.find(t => t.id === Number(ticketId));
        if (ticket && ticket.price > 0) {
          allFree = false;
          break;
        }
      }
    }

    return hasTickets && allFree;
  };

  // Validate form
  const validateForm = (): boolean => {
    const hasTickets = getTotalTicketCount() > 0;
    const exceedsLimit = getTotalTicketCount() > MAX_TICKETS_PER_PURCHASE;
    const hasValidEmail = isValidEmail(buyerEmail);
    const hasName = buyerName.trim().length > 0;

    if (exceedsLimit) {
      setCheckoutError(`You cannot purchase more than ${MAX_TICKETS_PER_PURCHASE} tickets in one order.`);
      return false;
    }

    if (!hasTickets) {
      setCheckoutError('Please select at least one ticket.');
      return false;
    }

    if (!hasValidEmail) {
      setCheckoutError('Please enter a valid email address (e.g., name@example.com).');
      return false;
    }

    if (!hasName) {
      setCheckoutError('Please enter your full name.');
      return false;
    }

    // Validate attendee emails
    for (const attendee of attendees) {
      if (attendee.email && !isValidEmail(attendee.email)) {
        setCheckoutError(`Invalid email for attendee: ${attendee.name || 'Unknown'}. Please fix before proceeding.`);
        return false;
      }
    }

    setCheckoutError(null);
    return true;
  };

  // Start reservation countdown
  useEffect(() => {
    if (reservationExpiresAt) {
      countdownInterval.current = setInterval(() => {
        const now = new Date();
        const timeLeft = reservationExpiresAt.getTime() - now.getTime();
        
        if (timeLeft <= 0) {
          setTimeRemaining(0);
          handleReservationExpired();
        } else {
          setTimeRemaining(timeLeft);
        }
      }, 1000);

      return () => {
        if (countdownInterval.current) {
          clearInterval(countdownInterval.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationExpiresAt]);

  // Format time remaining
  const formatTimeRemaining = (): string => {
    if (timeRemaining <= 0) return '0:00';
    const minutes = Math.floor(timeRemaining / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle reservation expired
  const handleReservationExpired = () => {
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current);
    }
    setCheckoutError('Your reservation has expired. Please try again.');
    setShowCheckout(false);
    setClientSecret(null);
    setSessionId(null);
    setReservationExpiresAt(null);
    clearLocalStorageSession();
  };

  // Process free booking
  const processFreeBooking = async () => {
    if (!validateForm() || !event || !eventUuid) return;

    setIsProcessing(true);
    setCheckoutError(null);

    const tickets = Object.entries(ticketCounts)
      .filter(([_, qty]) => qty > 0)
      .map(([ticketId, quantity]) => ({ ticket_id: Number(ticketId), quantity }));

    // Transform attendees to match API expectation
    const recipients = attendees.map(a => ({
      name: a.name,
      email: a.email,
      ticket_id: a.ticketId || 0, // Ensure strictly number
      quantity: 1
    }));

    // Construct customer params based on current user inputs
    const payload: any = {
      tickets,
      customer_email: buyerEmail, 
      customer_name: buyerName,
      recipients
    };

    // Include invite_token for private events
    console.log('Processing free booking with payload:', payload, 'Invite token:', inviteToken);
    if (inviteToken) {
      payload.invite_token = inviteToken;
    }

    // Include promo code if applied
    if (appliedPromoCode) {
      payload.promo_code = appliedPromoCode;
    }

    try {
      const response = await post<FreeBookingResponse>(SUBMIT_FREE_BOOKING_URL(eventUuid), payload);

      if (response.data.success) {
        setReceiptOrderId(generateReceiptOrderId(true));
        setFreeBookingSuccess(true);
        // No automatic redirect, user will click buttons in success modal
      }
    } catch (err: any) {
      setCheckoutError(err.response?.data?.error || 'Failed to complete free booking');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check eligibility for free booking
  const checkFreeBookingEligibility = async (): Promise<boolean> => {
    if (!eventUuid) return false;
    
    setIsProcessing(true);
    try {
      const selectedTicketIds = Object.keys(ticketCounts)
        .filter(key => ticketCounts[Number(key)] > 0)
        .map(Number);
        
      const response = await post<FreeBookingEligibilityResponse>(
        FREE_BOOKING_ELIGIBILITY_URL(eventUuid), 
        { ticket_ids: selectedTicketIds }
      );
      
      if (response.data.all_free) {
        return true;
      } else {
        // If not eligible (some tickets not free), maybe throw error or return false
        setCheckoutError('One or more selected tickets are not eligible for free booking.');
        return false;
      }
    } catch (err) {
      console.error('Eligibility check failed', err);
      return false; // Fallback to normal checkout or error?
    } finally {
      setIsProcessing(false);
    }
  };

  // Initialize Stripe checkout
  const initializeCheckout = async () => {
    if (!validateForm() || !event) return;

    // Generate reCAPTCHA token
    if (!executeRecaptcha) {
      setCheckoutError('reCAPTCHA not ready. Please try again.');
      return;
    }

    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha('checkout');
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      setCheckoutError('Failed to verify reCAPTCHA. Please try again.');
      return;
    }

    // Check if checks out as free on client side, verify with backend
    if (checkIfAllTicketsFree()) {
      const isEligible = await checkFreeBookingEligibility();
      if (isEligible) {
        setShowFreeBookingModal(true);
        return;
      }
      // If check fails (not actually free per backend), it falls through to paid checkout logic below
      // or we might want to stop if the user expected free?
      // Since button said "Book Free Tickets", falling through to Stripe might be confusing if prices changed.
      // But let's assume if it fails eligibility it might be because price > 0, so paid checkout handles it?
      // Actually if checkFreeBookingEligibility returns false, it sets an error if it was explicit check.
      // If we want to support mixed cart that looked free, we can let it fall through.
      // But typically if client thinks it's free, backend says no, we should probably alert user.
      if (checkoutError) return; // Exit if eligibility set an error
    }

    setIsProcessing(true);
    setCheckoutError(null);

    try {
      const tickets = Object.entries(ticketCounts)
        .filter(([_, qty]) => qty > 0)
        .map(([ticketId, quantity]) => ({ id: Number(ticketId), quantity }));

      const recipients = attendees.map(a => ({
        name: a.name,
        email: a.email,
        quantity: 1,
        ticket_id: a.ticketId
      }));

      const response = await post<CheckoutResponse>(PUBLIC_CHECKOUT_URL, {
        event_id: event.id,
        tickets,
        recipients,
        email: buyerEmail,
        name: buyerName,
        recaptcha_token: recaptchaToken,
        invite_token: inviteToken,
        ...(appliedPromoCode ? {
          promo_code: appliedPromoCode,
          discount_amount: parseFloat(calculateDiscountAmount().toFixed(2)),
          discounted_subtotal: parseFloat(calculateDiscountedSubtotal().toFixed(2)),
        } : {})
      });

      console.log('Checkout response:', response.data);

      // Handle case where user has an active session (200 OK with success=false)
      if (response.data.success === false && response.data.active_session) {
        setActiveSessionInfo(response.data.active_session);
        // Scroll to the active session warning
        document.querySelector('.active-session-warning')?.scrollIntoView({ behavior: 'smooth' });
        setIsProcessing(false);
        return;
      }

      // Process response data handling both camelCase and snake_case formats
      const r = response.data;
      const clientSecret = r.clientSecret || r.client_secret;
      const sessionId = r.sessionId || r.session_id;
      // Use backend key first, then fallback to env var
      const publishableKey = r.publishableKey || r.publishable_key || process.env.REACT_APP_STRIPE_PUBLIC_KEY;
      
      let reservationDate: Date | undefined;
      let reservationDateStr: string | undefined;

      if (r.reservationExpiresAt) {
        reservationDate = new Date(r.reservationExpiresAt);
        reservationDateStr = r.reservationExpiresAt;
      } else if (r.expires_at) {
        // Convert unix timestamp (seconds) to Date
        reservationDate = new Date(r.expires_at * 1000);
        reservationDateStr = reservationDate.toISOString();
      } else {
        // Default to 10 minutes from now if not provided by backend
        reservationDate = new Date(Date.now() + 10 * 60 * 1000);
        reservationDateStr = reservationDate.toISOString();
      }

      if (clientSecret && sessionId && publishableKey && reservationDate) {
        setClientSecret(clientSecret);
        setReservationExpiresAt(reservationDate);
        
        console.log('Loading Stripe with key:', publishableKey);
        console.log('Storing session info:', { sessionId, reservationDateStr });

        // Save to local storage explicitly before attempting Stripe load
        localStorage.setItem(LOCAL_STORAGE_SESSION_KEY, sessionId);
        localStorage.setItem(LOCAL_STORAGE_EXPIRY_KEY, reservationDateStr);
        // Also save email for recovery
        if (buyerEmail) {
            localStorage.setItem('customerEmail', buyerEmail);
        }
          
        try {
          // Load Stripe with the publishable key
          const stripe = await loadStripe(publishableKey);
          console.log('Stripe instance loaded:', stripe);
          
          if (stripe) {
            setStripeInstance(stripe);
            setSessionId(sessionId);
            setShowCheckout(true);
            console.log('Checkout should now be visible');
          } else {
             throw new Error("Failed to load Stripe instance (null returned)");
          }
        } catch (stripeError) {
          console.error("Stripe loading failed:", stripeError);
          
          // If Stripe fails to load, cancel the session immediately to avoid zombie sessions
          try {
             await post(CANCEL_ACTIVE_SESSION_URL, { 
                email: buyerEmail, 
                session_id: sessionId 
             });
             console.log('Cancelled session due to Stripe load failure');
          } catch (cancelError) {
             console.error('Failed to auto-cancel session:', cancelError);
          }
          
          // Clear local storage since we cancelled it
          clearLocalStorageSession();
          
          throw new Error("Failed to load payment system. Session cancelled.");
        }
      } else {
        console.error('Missing fields:', { clientSecret, sessionId, publishableKey, reservationDate });
        throw new Error('Missing required fields in response');
      }
    } catch (err: any) {
      if (err.response && err.response.status === 409) {
        // Handle 409 Conflict - Active Session
        const activeSession = err.response.data.active_session || err.response.data.activeSession;
        const errorMessage = err.response.data.message || 'You have an active session. Please attend to it above.';
        
        if (activeSession) {
          // Save session to local storage if it doesn't exist
          const storedSessionId = localStorage.getItem(LOCAL_STORAGE_SESSION_KEY);
          if (!storedSessionId && activeSession.session_id) {
            localStorage.setItem(LOCAL_STORAGE_SESSION_KEY, activeSession.session_id);
            // Save expiry based on created_at + 15 minutes (typical session duration)
            const sessionCreatedAt = new Date(activeSession.created_at);
            const expiryTime = new Date(sessionCreatedAt.getTime() + 15 * 60 * 1000);
            localStorage.setItem(LOCAL_STORAGE_EXPIRY_KEY, expiryTime.toISOString());
          }
          
          setActiveSessionInfo(activeSession);
          setCheckoutError(errorMessage);
          
          // Scroll to active session warning after a brief delay to ensure it's rendered
          setTimeout(() => {
            document.getElementById('active-session-warning')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          setCheckoutError(errorMessage);
        }
      } else {
        setCheckoutError(err.response?.data?.message || err.response?.data?.error || 'Failed to initialize checkout');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="event-details-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-details-page">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <h2>{error || 'Event not found'}</h2>
          <Link to="/events" className="back-btn">
            <i className="fas fa-arrow-left"></i> Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const mediaItems = getAllMedia();
  const isExternalEvent = event.event_ticketing_type === 'external';

  return (
    <div className="event-details-page split-layout">
      {/* Background image */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none select-none bg-cover bg-center bg-no-repeat bg-[#0a0a0f]" style={{backgroundImage: `url(${HomePageAssets.background})`, backgroundAttachment: 'fixed'}} />
      
      <ExternalEventModal
        isOpen={showExternalModal}
        onClose={() => setShowExternalModal(false)}
        onConfirm={handleExternalConfirm}
        externalUrl={event.external_ticketing_url || ''}
      />
      
      {/* Header */}
      <Header />
      
      {/* Back Button - Mobile First */}
      <Link to="/events" className="back-link-mobile">
        <i className="fas fa-arrow-left"></i> Back
      </Link>
      
      {/* Split Layout Container */}
      <div className="split-container">
        {/* Left Side - Scrollable Content */}
        <div className="left-content">
          <div className="content-wrapper">
            {/* Back Button - Desktop */}
            <Link to="/events" className="back-link-inline">
              <i className="fas fa-arrow-left"></i> Back
            </Link>

            {/* Event Header */}
            <div className="event-header">
              {inviteToken && (
                <div className="private-event-badge-details">
                  <i className="fas fa-lock"></i> Private Event
                </div>
              )}
              
              <div className="event-date-inline">
                <i className="far fa-calendar"></i>
                <span>{formatDateShort(event.start, event.timezone)} • {formatTime(event.start, event.timezone)}</span>
              </div>
              <h1 className="event-title-main">{event.event_title}</h1>
            </div>

            {/* Expired Event Banner */}
            {isEventExpired() && (
              <div className="external-event-banner" style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' }}>
                <div className="external-event-banner-content">
                  <div className="external-event-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="external-event-text">
                    <h3>This event has ended</h3>
                    <p>Ticket sales are no longer available for this event</p>
                  </div>
                </div>
              </div>
            )}

            {/* External Event Banner */}
            {isExternalEvent && event.tickets && event.tickets.length === 0 &&  (
              <div className="external-event-banner">
                <div className="external-event-banner-content">
                  <div className="external-event-icon">
                    <i className="fas fa-ticket-alt"></i>
                  </div>
                  <div className="external-event-text">
                    <h3>This event is hosted externally</h3>
                    <p>Ticket sales and event details are managed by the organizer, not Musicade</p>
                  </div>
                </div>
                <button className="visit-event-website-btn" onClick={handleExternalClick}>
                  Visit Event Website <i className="fas fa-external-link-alt"></i>
                </button>
              </div>
            )}

            {/* About This Event */}
            {!isExternalEvent && (
              <div className="section-plain">
                <h2 className="section-title-plain">About this Event</h2>
                <p className="event-description-plain">{event.info}</p>
              </div>
            )}

            {/* Location */}
            {event.event_type === 'physical' && (
              <div className="section-plain">
                <div className="location-header">
                  <h2 className="section-title-plain">Location</h2>
                  <button className="get-direction-btn" onClick={handleGetDirection}>
                    Get direction 
                    <span className="btn-icon-box">
                      <i className="fas fa-arrow-right"></i>
                    </span>
                  </button>
                </div>
                <p className="location-address">
                  {event.venue && `${event.venue}, `}
                  {event.city}, {event.state}, {event.country}
                </p>
                
                {/* Map View */}
                <div className="map-container">
                  {event.location_coordinates?.latitude && event.location_coordinates?.longitude ? (
                    <MapContainer
                      center={[event.location_coordinates.latitude, event.location_coordinates.longitude]}
                      zoom={15}
                      style={{ height: '100%', width: '100%', borderRadius: '12px' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[event.location_coordinates.latitude, event.location_coordinates.longitude]}>
                        <Popup>
                          <strong>{event.event_title}</strong><br />
                          {event.venue && `${event.venue}, `}
                          {event.city}, {event.state}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="map-placeholder">
                      <i className="fas fa-map-marked-alt"></i>
                      <p>Map View Unavailable</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Select Tickets */}
            {!isExternalEvent && !isEventExpired() && (
            <div className="section-plain">
              <h2 className="section-title-plain">Select Tickets</h2>
              
              {/* Active Session Warning */}
              {activeSessionInfo && (
                <div id="active-session-warning" className="active-session-warning">
                  <h4><i className="fas fa-exclamation-triangle"></i> Active Purchase Session</h4>
                  <p>You have an active checkout session from {new Date(activeSessionInfo.created_at).toLocaleString()}.</p>
                  {activeSessionInfo.email && (
                    <p className="session-email"><i className="fas fa-envelope"></i> {activeSessionInfo.email}</p>
                  )}
                  {checkoutError && (
                    <p className="session-error-message"><i className="fas fa-info-circle"></i> {checkoutError}</p>
                  )}
                  <div className="button-group">
                    <button 
                      onClick={() => resumeSession(activeSessionInfo.session_id)} 
                      className="resume-btn"
                      disabled={isProcessing}
                    >
                      {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-play"></i>} Resume Session
                    </button>
                    <button 
                      onClick={() => setShowCancelConfirmation(true)} 
                      className="cancel-btn"
                      disabled={isProcessing}
                    >
                      <i className="fas fa-times"></i> Cancel & Start New
                    </button>
                  </div>
                </div>
              )}

              {event.tickets && event.tickets.length > 0 ? (
                <div className="tickets-grid">
                  {event.tickets.map((ticket) => (
                    <div key={ticket.id} className="ticket-card">
                      <div className="ticket-price-main">${ticket.price.toLocaleString()}</div>
                      <div className="ticket-content-row">
                        <div className="ticket-info-left">
                          <h3 className="ticket-type">{ticket.ticket_type}</h3>
                          <p className="ticket-desc">{ticket.description}</p>
                          <p className="ticket-meta">
                            Ticket is non-refundable.
                            <br />
                            {getTicketSaleMessage(ticket, event.timezone) ?? 'Ticket information unavailable.'}
                          </p>
                          <p className="ticket-remaining" style={{ fontSize: '0.85rem', color: ticket.remaining_tickets < 10 ? '#ef5350' : '#9ca3af', marginTop: '4px', fontWeight: 500 }}>
                            <i className="fas fa-ticket-alt" style={{ marginRight: '6px' }}></i>
                            {ticket.remaining_tickets === 0 ? 'Sold Out' : `${ticket.remaining_tickets} tickets remaining`}
                          </p>
                        </div>
                        <div className="ticket-action-right">
                          {ticketCounts[ticket.id] ? (
                            <div className="ticket-counter">
                              <button 
                                className="counter-btn minus-btn" 
                                onClick={() => handleRemoveTicket(ticket.id)}
                              >
                                <i className="fas fa-minus"></i>
                              </button>
                              <span className="counter-display">{ticketCounts[ticket.id]}</span>
                              <button 
                                className="counter-btn plus-btn" 
                                onClick={() => handleAddTicket(ticket.id)}
                                disabled={(ticketCounts[ticket.id] || 0) >= ticket.remaining_tickets}
                                style={{ opacity: (ticketCounts[ticket.id] || 0) >= ticket.remaining_tickets ? 0.5 : 1 }}
                              >
                                <i className="fas fa-plus"></i>
                              </button>
                            </div>
                          ) : (
                            <button 
                              className="add-ticket-btn"
                              onClick={() => handleAddTicket(ticket.id)}
                              disabled={ticket.remaining_tickets === 0}
                              style={ticket.remaining_tickets === 0 ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#333' } : {}}
                            >
                              <i className="fas fa-plus"></i> {ticket.remaining_tickets === 0 ? 'Sold Out' : 'Add Ticket'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-tickets">No tickets available at the moment</p>
              )}
            </div>
            )}

            {/* Who's Buying Section */}
            {!isExternalEvent && !isEventExpired() && getTotalTicketCount() > 0 && (
              <div className="section-plain checkout-section">
                <div className="checkout-header">
                  <h2 className="section-title-plain">Who's Buying?</h2>
                  <div className="tickets-available-badge">
                    <i className="fas fa-ticket-alt"></i>
                    {getTotalTicketCount()} ticket{getTotalTicketCount() > 1 ? 's' : ''} available
                  </div>
                </div>

                <div className="buyer-form">
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Enter your email address"
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Your full name"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Who's Going With You - Hidden for private events */}
                {!inviteToken && (
                  <div className="attendees-section">
                    <div className="attendees-header">
                      <h3 className="attendees-title">Who's going with you?</h3>
                      {getAttendeesCount() > 0 && (
                        <span className="attendees-count-badge">{getAttendeesCount()}</span>
                      )}
                    </div>
                    <p className="attendees-subtitle">Add the details of anyone else who should receive a ticket.</p>

                    {attendees.length > 0 && (
                      <div className="attendees-list">
                        {attendees.map((attendee) => (
                          <div key={attendee.id} className="attendee-card">
                            <div className="attendee-avatar">
                              <i className="fas fa-user"></i>
                            </div>
                            <div className="attendee-info">
                              <div className="attendee-name">{attendee.name || 'No name'}</div>
                              <div className="attendee-email">{attendee.email || 'No email'}</div>
                            </div>
                            <div className="attendee-actions">
                              <button
                                className="edit-attendee-btn"
                                onClick={() => handleEditAttendee(attendee)}
                              >
                                <i className="fas fa-pen"></i>
                              </button>
                              <button
                                className="delete-attendee-btn"
                                onClick={() => handleRemoveAttendee(attendee.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <button className="add-someone-btn" onClick={handleAddAttendee}>
                      <i className="fas fa-user-plus"></i> Add Someone
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Order Summary Section */}
            {!isEventExpired() && getTotalTicketCount() > 0 && (
              <div className="section-plain order-summary-section">
                <h2 className="section-title-plain">Order Summary</h2>
                
                <div className="order-items">
                  {event?.tickets && event.tickets.map((ticket) => {
                    const count = ticketCounts[ticket.id] || 0;
                    if (count === 0) return null;
                    return (
                      <div key={ticket.id} className="order-item">
                        <span className="order-item-name">
                          {ticket.ticket_type} {count > 1 && `(x${count})`}
                        </span>
                        <span className="order-item-price">
                          ${(ticket.price * count).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="order-calculations">
                  <div className="order-row">
                    <span className="order-label">Subtotal</span>
                    <span className="order-value">${calculateSubtotal().toFixed(2)}</span>
                  </div>
                  {/* Promo discount row + discounted subtotal — visible only when a valid promo is applied */}
                  {appliedPromoCode && calculateDiscountAmount() > 0 && (
                    <>
                      <div className="order-row discount-row">
                        <span className="order-label discount-label">
                          <i className="fas fa-tag"></i> Promo Discount ({appliedPromoCode}):
                        </span>
                        <span className="order-value discount-value">
                          -${calculateDiscountAmount().toFixed(2)}
                        </span>
                      </div>
                      <div className="order-row discounted-subtotal-row">
                        <span className="order-label">Discounted Subtotal</span>
                        <span className="order-value">${calculateDiscountedSubtotal().toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="order-row">
                    <span className="order-label">Platform Fee (7%):</span>
                    <span className="order-value">${calculatePlatformFee(calculateDiscountedSubtotal()).toFixed(2)}</span>
                  </div>
                  <div className="order-row">
                    <span className="order-label">Processing Fee:</span>
                    <span className="order-value">${calculateProcessingFee(calculateDiscountedSubtotal()).toFixed(2)}</span>
                  </div>
                </div>

                {/* Promo Code Section */}
                {!checkIfAllTicketsFree() && (
                  <div className="promo-code-section">
                    {!appliedPromoCode ? (
                      <>
                        <div className="promo-code-input-row">
                          <div className="promo-code-input-wrapper">
                            <i className="fas fa-tag promo-icon"></i>
                            <input
                              type="text"
                              className="promo-code-input"
                              placeholder="Enter promo code"
                              value={promoCode}
                              onChange={(e) => {
                                setPromoCode(e.target.value.toUpperCase());
                                setPromoError(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') validatePromoCode();
                              }}
                              disabled={promoValidating}
                            />
                          </div>
                          <button
                            className="apply-promo-btn"
                            onClick={validatePromoCode}
                            disabled={!promoCode.trim() || promoValidating}
                          >
                            {promoValidating ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              'Apply'
                            )}
                          </button>
                        </div>
                        {promoError && (
                          <div className="promo-error">
                            <i className="fas fa-times-circle"></i> {promoError}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="promo-applied">
                        <div className="promo-applied-info">
                          <i className="fas fa-check-circle promo-check-icon"></i>
                          <span className="promo-code-badge">{appliedPromoCode}</span>
                          <span className="promo-applied-text">applied successfully</span>
                        </div>
                        <button className="remove-promo-btn" onClick={removePromoCode}>
                          <i className="fas fa-times"></i> Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="order-total">
                  <span className="order-total-label">Total:</span>
                  <span className="order-total-value">
                    ${calculateTotal().toFixed(2)}
                  </span>
                </div>

                {/* Checkout Error */}
                {checkoutError && (
                  <div className="checkout-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {checkoutError}
                  </div>
                )}

                {/* Reservation Timer */}
                {reservationExpiresAt && (
                  <div className={`reservation-timer ${timeRemaining < 180000 ? 'warning' : ''}`}>
                    <i className="fas fa-clock"></i>
                    <span>Time remaining: {formatTimeRemaining()}</span>
                  </div>
                )}

                {!showCheckout && !isEventExpired() && (
                  <button 
                    className="proceed-payment-btn"
                    onClick={initializeCheckout}
                    disabled={isProcessing || getTotalTicketCount() === 0 || !!activeSessionInfo || promoValidating}
                  >
                    {isProcessing ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Processing...
                      </>
                    ) : promoValidating ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Validating Promo...
                      </>
                    ) : activeSessionInfo ? (
                      <>
                        <i className="fas fa-clock"></i> Session Active
                      </>
                    ) : checkIfAllTicketsFree() ? (
                      <>
                        <i className="fas fa-ticket-alt"></i> Book Free Tickets
                      </>
                    ) : (
                      <>
                        <i className="fas fa-lock"></i> Proceed to Payment
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            
           

            {/* Embedded Checkout */}
            {showCheckout && clientSecret && stripeInstance && (
              <div className="modal-overlay" style={{ zIndex: 2000 }}>
                <div className="modal-content checkout-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                  <div className="modal-header">
                    <h3 className="modal-title">Complete Your Purchase</h3>
                    <button 
                      className="modal-close-btn"
                      onClick={() => {
                        // User closed the modal manually without completing payment.
                        // Show active session warning so they can resume or cancel.
                        setShowCheckout(false);
                        setClientSecret(null);
                        setStripeInstance(null);
                        checkLocalStorageSession();
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="modal-body" style={{ padding: '0 1rem 1rem' }}>
                    <EmbeddedCheckoutProvider
                      stripe={stripeInstance}
                      options={{ clientSecret }}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </div>
                </div>
              </div>
            )}
            
            {/* Debug info */}
            {showCheckout && !clientSecret && (
              <div className="checkout-error">
                <i className="fas fa-exclamation-circle"></i>
                Waiting for client secret...
              </div>
            )}
            {showCheckout && clientSecret && !stripeInstance && (
              <div className="checkout-error">
                <i className="fas fa-exclamation-circle"></i>
                Loading Stripe...
              </div>
            )}

            {/* Organizer Section */}
            {event.event_creator && (
              <div className="organizer-section">
                <div className="organizer-header">
                  <div className="organizer-info-left">
                    <div className="organizer-avatar">
                      {event.event_creator.profile_image ? (
                        <img src={event.event_creator.profile_image} alt={event.event_creator.full_name} />
                      ) : (
                        <i className="fas fa-user"></i>
                      )}
                    </div>
                    <div className="organizer-details">
                      <h3 className="organizer-name">{event.event_creator.full_name || event.event_creator.username}</h3>
                    </div>
                  </div>
                  <Link to={`/profile/${event.event_creator.username}`} className="view-profile-link">
                    View Profile <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>

                <div className="organizer-promo">
                  <div className="organizer-qr">
                    <div className="qr-code-box">
                      <QRCodeSVG 
                        value="https://apps.apple.com/us/app/musicade-app/id6747211062" 
                        size={120}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    <p className="qr-scan-text">Scan to download</p>
                  </div>
                  <div className="organizer-promo-text">
                    <h4 className="promo-title">Discover more organizers like this</h4>
     <a      href="https://apps.apple.com/us/app/musicade/id6747211062?uo=4"   data-umami-event="Event Detail Page Footer" target="_blank" rel="noopener noreferrer" className="inline-block">
          <img src={HomePageAssets.appleBadge} alt="App Store Badge" className=" inline-block  " />
        </a>
                  </div>
                </div>
              </div>
            )}

            {/* Attendee Modal */}
            {showAttendeeModal && modalAttendee && (
              <div className="modal-overlay" onClick={() => setShowAttendeeModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Recipient Information</h3>
                    <button className="modal-close-btn" onClick={() => setShowAttendeeModal(false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="modal-body">
                    {modalError && (
                      <div className="checkout-error" style={{ marginBottom: '1rem' }}>
                        <i className="fas fa-exclamation-circle"></i> {modalError}
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter Name"
                        value={modalAttendee.name}
                        onChange={(e) => handleModalInputChange('name', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="Enter email address"
                        value={modalAttendee.email}
                        onChange={(e) => handleModalInputChange('email', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Choose Tickets</label>
                      <select
                        className="form-select"
                        value={modalAttendee.ticketId || ''}
                        onChange={(e) => handleModalInputChange('ticketId', e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">Select</option>
                        {getSelectedTickets().map((ticket) => (
                          <option key={ticket.id} value={ticket.id}>
                            {ticket.ticket_type} - ${ticket.price}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button className="modal-save-btn" onClick={handleSaveAttendee}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Free Booking Confirmation Modal */}
            {showFreeBookingModal && !freeBookingSuccess && (
              <div className="modal-overlay" onClick={() => setShowFreeBookingModal(false)}>
                <div className="modal-content free-booking-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">
                      <i className="fas fa-ticket-alt"></i> Confirm Free Booking
                    </h3>
                    <button className="modal-close-btn" onClick={() => setShowFreeBookingModal(false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="modal-body">
                    <p className="confirmation-text">
                      You are about to book free tickets. Your confirmation will be sent to <strong>{buyerEmail}</strong>
                    </p>

                    <div className="modal-actions">
                      <button 
                        className="modal-cancel-btn"
                        onClick={() => setShowFreeBookingModal(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="modal-confirm-btn"
                        onClick={async () => {
                          setShowFreeBookingModal(false);
                          await processFreeBooking();
                        }}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i> Processing...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check"></i> Confirm Booking
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Receipt (Free or Paid) */}
            {(freeBookingSuccess || checkoutSuccess) && event && (
              <div className="modal-overlay" style={{ zIndex: 2100 }}>
                <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-body text-center">
                    <div className="success-icon-large">
                      <i className="fas fa-check"></i>
                    </div>
                    <h3 className="success-title">Ticket Purchase Receipt</h3>
                    <p className="success-text">
                      Thank you for your purchase. Your tickets for <strong>{event.event_title}</strong> has been secured. A confirmation email has been sent to <strong>{buyerEmail}</strong> with your ticket details
                    </p>

                    <h4 className="summary-title"><i className="fas fa-receipt"></i> Order Summary</h4>
                    
                    <div className="success-order-number">
                      Order Number: <span className="order-id">{receiptOrderId || generateReceiptOrderId(freeBookingSuccess)}</span>
                    </div>

                    <div className="success-event-card">
                      <div className="success-event-row">
                        <i className="far fa-calendar-alt"></i>
                        <span className="success-label">Event</span>
                        <span className="success-value">{event.event_title}</span>
                      </div>
                      <div className="success-event-row">
                        <i className="far fa-calendar"></i>
                        <span className="success-label">Date</span>
                        <span className="success-value">{formatDateShort(event.start, event.timezone)}</span>
                      </div>
                      <div className="success-event-row">
                        <i className="far fa-clock"></i>
                        <span className="success-label">Time</span>
                        <span className="success-value">{formatTime(event.start, event.timezone)}</span>
                      </div>
                      <div className="success-event-row">
                        <i className="fas fa-map-marker-alt"></i>
                        <span className="success-label">Venue</span>
                        <span className="success-value">{event.venue || event.city}, {event.city}, {event.state}, {event.country}</span>
                      </div>
                    </div>

                    <div className="success-financials">
                      <div className="financial-row">
                        <span>Subtotal</span>
                        <span>${freeBookingSuccess ? '0.00' : calculateSubtotal().toFixed(2)}</span>
                      </div>
                      <div className="financial-row">
                        <span>Processing Fee</span>
                        <span>${freeBookingSuccess ? '0.00' : calculateProcessingFee(calculateSubtotal()).toFixed(2)}</span>
                      </div>
                      <div className="financial-row total">
                        <span>Total Paid</span>
                        <span>${freeBookingSuccess ? '0.00' : calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="success-actions">
                      <button
                        className="cancel-receipt-btn"
                        onClick={() => {
                          // Reload page to refresh ticket counts and reset state completely
                          window.location.reload();
                        }}
                      >
                        Cancel
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {/* Cancel Confirmation Modal */}
            {showCancelConfirmation && (
              <div className="modal-overlay" onClick={() => setShowCancelConfirmation(false)}>
                <div className="modal-content free-booking-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">
                      <i className="fas fa-exclamation-triangle" style={{color: '#ff9800'}}></i> Cancel Active Session?
                    </h3>
                    <button className="modal-close-btn" onClick={() => setShowCancelConfirmation(false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="modal-body">
                    <p className="confirmation-text">
                      Are you sure you want to cancel your previous checkout session? <br/>
                      This action cannot be undone and your held tickets will be released.
                    </p>

                    <div className="modal-actions">
                      <button 
                        className="modal-cancel-btn"
                        onClick={() => setShowCancelConfirmation(false)}
                        disabled={isProcessing}
                      >
                        No, Keep Session
                      </button>
                      <button 
                        className="modal-confirm-btn"
                        style={{backgroundColor: '#e53935'}}
                        onClick={cancelActiveSession}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash-alt"></i>} Yes, Cancel It
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Fixed Media & Info */}
        <div className="right-sidebar">
          {/* Media Carousel */}
          <div className="media-container">
            {mediaItems.length > 0 ? (
              <>
                <div className="media-display">
                  {mediaItems[currentMediaIndex].type === 'video' ? (
                    <video 
                      src={mediaItems[currentMediaIndex].url}
                      controls
                      className="media-item"
                    />
                  ) : (
                    <img 
                      src={mediaItems[currentMediaIndex].url}
                      alt={event.event_title}
                      className="media-item"
                      onClick={() => setShowLightbox(true)}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </div>
                
                {/* Thumbnail List */}
                {mediaItems.length > 1 && (
                  <div className="media-thumbnails">
                    {mediaItems.map((item, index) => (
                      <button
                        key={index}
                        className={`thumbnail-btn ${index === currentMediaIndex ? 'active' : ''}`}
                        onClick={() => setCurrentMediaIndex(index)}
                      >
                        {item.type === 'video' ? (
                          <div className="thumbnail-video">
                            <i className="fas fa-play-circle"></i>
                            <video src={item.url} className="thumbnail-item" />
                          </div>
                        ) : (
                          <img 
                            src={item.url}
                            alt={`Thumbnail ${index + 1}`}
                            className="thumbnail-item"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="media-placeholder">
                <i className="fas fa-calendar-alt"></i>
              </div>
            )}
          </div>

          {/* Event Details Card */}
          <div className="event-info-card">
            <div className="event-date-time">
              <i className="far fa-calendar"></i>
              <span>{formatDateShort(event.start, event.timezone)} • {formatTime(event.start, event.timezone)}</span>
            </div>
            
            <h1 className="event-title-sidebar">{event.event_title}</h1>
            
            {/* Collaborators Section */}
            {event.collaborators && event.collaborators.length > 0 && (
              <div className="collaborators-section">
                <h3 className="collaborators-title">Collaborators</h3>
                <div className="collaborators-list">
                  {/* Event Creator as Organizer */}
                  <div className="collaborator-item">
                    <img 
                      src={event.event_creator.profile_image} 
                      alt={event.event_creator.full_name}
                      className="collaborator-avatar"
                    />
                    <div className="collaborator-details">
                      <p className="collaborator-name">{event.event_creator.full_name}</p>
                      <span className="collaborator-role organizer">Organizer</span>
                    </div>
                  </div>
                  
                  {/* Other Collaborators */}
                  {event.collaborators.map((collaborator, index) => (
                    <div key={collaborator.id} className="collaborator-item">
                      <img 
                        src={collaborator.profile_image} 
                        alt={collaborator.full_name}
                        className="collaborator-avatar"
                      />
                      <div className="collaborator-details">
                        <p className="collaborator-name">{collaborator.full_name}</p>
                        <span className="collaborator-role">
                          {index === 0 ? 'Host / MC' : index === 1 ? 'DJ' : 'Collaborator'}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {/* Show More Button if there are many collaborators */}
                  {event.collaborators.length > 2 && (
                    <button className="show-more-btn">
                      <i className="fas fa-chevron-down"></i>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showLightbox && mediaItems.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
          <button className="lightbox-close-btn" onClick={() => setShowLightbox(false)}>
            <i className="fas fa-times"></i>
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
             {mediaItems[currentMediaIndex].type === 'video' ? (
                <video src={mediaItems[currentMediaIndex].url} controls className="lightbox-media" />
             ) : (
                <img src={mediaItems[currentMediaIndex].url} alt="Full view" className="lightbox-media" />
             )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
